import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { getEstimatedTotalTransferValue } from '@sharedUtils';
import {
  CalculateEstimatedValueOfTransfer,
  CalculateEstimatedValueOfTransferFail,
  CalculateEstimatedValueOfTransferSuccess,
  CalculateSharesToTransfer,
  CalculateSharesToTransferFail,
  CalculateSharesToTransferSuccess,
  CalculateTotalEstimatedAmount,
  CalculateTotalEstimatedAmountFail,
  closeSpinner,
  LoadInternalTransferCalculations,
  LoadInternalTransferCalculationsSuccess,
  LogError,
  openSpinner,
  UpdateInternalTransferTotalTransferValue,
  UpdateSelectedPositions,
} from '@store/actions';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { InternalTransferMethodEnum } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';
import {
  selectTransferMethod,
  selectTransferType,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  getEstimatedValueOfTransferByPercent,
  getEstimatedValueOfTransferByShares,
  getSharesToTransfer,
} from '../../../../shared/helpers/internal-transfer-calculations';
import { math } from '../../../../shared/helpers/mathjs/mathjs';
import * as fromRootStore from '../../index';

@Injectable()
export class InternalTransferCalculationsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
  ) {}

  loadInternalTransferCalculations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadInternalTransferCalculations),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(() => {
        return [CalculateSharesToTransfer()];
      }),
    ),
  );

  calculateSharesToTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalculateSharesToTransfer),
      withLatestFrom(
        this.store$.pipe(select(selectTransferMethod)),
        this.store$.pipe(select(selectSelectedPositions)),
        this.store$.pipe(select(selectTransferType)),
      ),
      switchMap(([, transferMethod, selectedPositions, transferType]) => {
        const updatedPositions: CostBasisPosition[] = selectedPositions.map((position) =>
          getSharesToTransfer(+position.percentOfHolding, position, transferType, transferMethod),
        );

        return [
          CalculateEstimatedValueOfTransfer({ payload: updatedPositions }),
          CalculateSharesToTransferSuccess(),
        ];
      }),
      catchError(() => {
        return [
          CalculateSharesToTransferFail(),
          LogError({
            payload: {
              eventId: 'calculate-shares-to-transfer-fail-ITCalculationsEffect',
              message: 'Failed to calculate shares to transfer',
            },
          }),
        ];
      }),
    ),
  );

  calculateEstimatedValueOfTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalculateEstimatedValueOfTransfer),
      withLatestFrom(this.store$.pipe(select(selectTransferMethod))),
      switchMap(([action, transferMethod]) => {
        const positions: CostBasisPosition[] = action.payload;
        const updatedPositions: CostBasisPosition[] =
          transferMethod !== InternalTransferMethodEnum.PERCENT
            ? positions.map((position) => getEstimatedValueOfTransferByShares(position))
            : positions.map((position) =>
                getEstimatedValueOfTransferByPercent(Number(position.percentOfHolding), position),
              );

        return [
          UpdateSelectedPositions({ payload: updatedPositions }),
          CalculateTotalEstimatedAmount({ payload: updatedPositions }),
          CalculateEstimatedValueOfTransferSuccess(),
        ];
      }),
      catchError(() => {
        return [
          CalculateEstimatedValueOfTransferFail(),
          LogError({
            payload: {
              eventId: 'calculate-estimated-value-of-transfer-fail-ITCalculationsEffect',
              message: 'Failed to calculate estimated value of transfer for holding',
            },
          }),
        ];
      }),
    ),
  );

  calculateTotalEstimatedAmount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalculateTotalEstimatedAmount),
      switchMap((action) => {
        const positions = action.payload;
        const estimatedTotalValue: number = getEstimatedTotalTransferValue(positions);

        return [
          UpdateInternalTransferTotalTransferValue({
            payload: String(math.format(estimatedTotalValue, { notation: 'fixed', precision: 2 })),
          }),
          LoadInternalTransferCalculationsSuccess(),
        ];
      }),
      catchError(() => {
        return [
          CalculateTotalEstimatedAmountFail(),
          LogError({
            payload: {
              eventId: 'calculate-total-estimated-amount-fail-ITCalculationsEffect',
              message: 'Failed to calculate total estimated transfer amount',
            },
          }),
        ];
      }),
    ),
  );

  loadInternalTransferCalculationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadInternalTransferCalculationsSuccess),
      switchMap(() => {
        return [closeSpinner()];
      }),
    ),
  );
}
